import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { FileText, MoreVertical, Plus, Target } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import {
  type KeyResultCreate,
  createCheckin,
  createKeyResult,
  deleteKeyResult,
  getObjectiveBySlug,
  patchKeyResult,
  patchObjective,
} from "@/api/plan";
import type { KeyResultRead } from "@chai/shared";
import { canEdit, canManageMembers } from "@/components/chat/railMode";
import { LinkedIntegrations } from "@/components/plan/LinkedIntegrations";
import { SpaceMembersButton } from "@/components/plan/SpaceMembersButton";
import { DeskScreen } from "@/components/plan/DeskScreen";
import { ProgressBar } from "@/components/plan/ProgressBar";
import { StatusPill } from "@/components/plan/StatusPill";
import { confidenceLabel, pillClass } from "@/components/plan/plan-status";
import { useDocumentTitle } from "@/lib/document-title";

export const Route = createFileRoute("/_app/plan/o/$objectiveSlug/")({
  component: ObjectiveDetail,
});

const CONFIDENCE_OPTIONS = ["on_track", "at_risk", "off_track"] as const;

/** Objective goal type — Committed vs Aspirational. Drives the meta dot/label and
 * the ⋮ "Goal type" submenu (mockup goalType.js). */
const GOAL_TYPES = [
  {
    key: "committed",
    label: "Committed goal",
    help: "Expected to fully land. Lower progress near the deadline is serious.",
  },
  {
    key: "aspirational",
    label: "Stretch goal",
    help: "Ambitious stretch goal — 60–70% can still be a strong outcome.",
  },
] as const;
const goalOf = (v: string | null | undefined) =>
  GOAL_TYPES.find((g) => g.key === v) ?? GOAL_TYPES[0];

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(1));

const initials = (name: string) =>
  (name || "")
    .split(/[\\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("") || "?";

/** Weighted Objective progress roll-up — DERIVED client-side from each KR's
 * weight + progress (mirrors the mockup's okrWeight.rollup). A KR carrying a
 * positive weight uses it as its share; KRs without one split the remainder up
 * to 100 (or 0 if the set weights already exceed it). Shares are normalized so
 * they always sum to ~100%. Contribution = progress × normalized share. */
function rollup(krs: KeyResultRead[]) {
  const active = krs.filter(Boolean);
  if (active.length === 0) return { progress: 0, weighted: false, items: [] as RollItem[] };

  const raw = active.map((k) => (typeof k.weight === "number" && k.weight > 0 ? k.weight : null));
  const weighted = raw.some((w) => w != null);

  let eff: number[];
  if (!weighted) {
    eff = active.map(() => 1); // equal weighting
  } else {
    const setSum = raw.reduce<number>((a, w) => a + (w ?? 0), 0);
    const nNull = raw.filter((w) => w == null).length;
    const fill = nNull > 0 ? Math.max(0, 100 - setSum) / nNull : 0;
    eff = raw.map((w) => (w != null ? w : fill));
  }

  const sum = eff.reduce((a, b) => a + b, 0) || 1;
  const items: RollItem[] = active.map((k, i) => {
    const share = (eff[i] ?? 0) / sum;
    const progress = k.progress || 0;
    return {
      id: k.id,
      ref: `KR-${i + 1}`,
      name: k.name,
      progress,
      weightPct: Math.round(share * 100),
      contribution: progress * share,
    };
  });
  const progress = Math.round(items.reduce((a, it) => a + it.contribution, 0));
  return { progress, weighted, items };
}

interface RollItem {
  id: string;
  ref: string;
  name: string;
  progress: number;
  weightPct: number;
  contribution: number;
}

/** "2 On Track · 1 At Risk · 1 No signal" — non-zero confidence buckets only. */
function confSummaryText(krs: KeyResultRead[]): string {
  const c = { on_track: 0, at_risk: 0, off_track: 0, none: 0 };
  for (const k of krs) {
    if (k.confidence === "on_track") c.on_track++;
    else if (k.confidence === "at_risk") c.at_risk++;
    else if (k.confidence === "off_track") c.off_track++;
    else c.none++;
  }
  const parts: string[] = [];
  if (c.on_track) parts.push(`${c.on_track} On Track`);
  if (c.at_risk) parts.push(`${c.at_risk} At Risk`);
  if (c.off_track) parts.push(`${c.off_track} Off Track`);
  if (c.none) parts.push(`${c.none} No signal`);
  return parts.join(" · ");
}

/** Whole days elapsed since an ISO timestamp (>= 0). */
const daysSince = (iso: string): number => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
};

/** Objective "why" line — DERIVED client-side from each KR's confidence + the
 * freshest last check-in (mirrors the mockup's objReason in ObjectiveCanvas).
 * "N KR(s) at risk · last check-in Xd ago" when any KR is at/off track, else
 * "No blockers · …". The MOST overdue KR drives the staleness phrasing. */
function objStatusReason(
  krs: KeyResultRead[],
  goalKey: string,
  status: string,
  progress: number,
): string {
  const risky = krs.filter(
    (k) => k.confidence === "at_risk" || k.confidence === "off_track",
  ).length;
  const days = krs
    .map((k) => k.last_checkin?.created_at)
    .filter((d): d is string => !!d)
    .map(daysSince);
  const tail =
    days.length === 0
      ? "no check-ins yet"
      : Math.max(...days) > 7
        ? `no check-in in ${Math.max(...days)} days`
        : `last check-in ${Math.min(...days)}d ago`;
  if (goalKey === "aspirational" && status === "on_track" && progress < 50) {
    return "Early-stage stretch goal · progress expected later";
  }
  if (risky > 0) return `${risky} KR${risky > 1 ? "s" : ""} at risk · ${tail}`;
  return `No blockers · ${tail}`;
}

function ObjectiveDetail() {
  const { objectiveSlug } = useParams({ from: "/_app/plan/o/$objectiveSlug/" });
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const key = ["plan", "objective-by-slug", objectiveSlug];

  const detail = useQuery({ queryKey: key, queryFn: () => getObjectiveBySlug(objectiveSlug) });

  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: ["plan", "objectives"] });
  };

  const addKr = useMutation({
    mutationFn: (b: KeyResultCreate) => createKeyResult(detail.data!.objective.id, b),
    onSuccess: () => {
      invalidate();
      setAddOpen(false);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const patchObj = useMutation({
    mutationFn: (b: Parameters<typeof patchObjective>[1]) =>
      patchObjective(detail.data!.objective.id, b),
    onSuccess: invalidate,
    onError: (e) => toast.error((e as Error).message),
  });

  const archiveObj = useMutation({
    mutationFn: () => patchObjective(detail.data!.objective.id, { archived: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["plan", "objectives"] });
      toast.success("Objective archived");
      void nav({ to: "/plan/all-work" });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const crumb = <div className="crumb">Objective</div>;

  useDocumentTitle([detail.data?.objective.name ?? "Objective", "Objective"]);

  if (detail.isLoading) {
    return (
      <DeskScreen>
        {crumb}
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-28 w-full" />
      </DeskScreen>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <DeskScreen>
        {crumb}
        <p className="text-[13px] text-[var(--color-semantic-error)]">
          {(detail.error as Error)?.message ?? "Objective not found."}
        </p>
      </DeskScreen>
    );
  }

  const { objective, key_results } = detail.data;
  // Page-body write gate (editor+). Viewers/commenters see read-only content; the
  // create/edit/destructive controls are hidden. Backend is the hard gate.
  const myRole = detail.data.my_role;
  const mayEdit = canEdit(myRole);
  const mayManageMembers = canManageMembers(myRole);
  const objectiveId = objective.id;
  const ownerName = objective.owner_name ?? null;
  const gt = goalOf(objective.goal_type);

  // Objective progress is DERIVED from the KR roll-up (weighted or averaged),
  // not the stored objective.progress — so it tracks the breakdown table 1:1.
  const roll = rollup(key_results);
  const headlinePct = roll.items.length > 0 ? roll.progress : objective.progress;

  const commitRename = () => {
    const v = nameDraft.trim();
    if (v && v !== objective.name) patchObj.mutate({ name: v });
    setEditingName(false);
  };

  // ── Above the sheet (desk): the objective control card ─────────────────────
  const toolbar = (
    <div className="desk-head">
      <div className="crumb">Objective</div>

      <div className="obj-cc">
        {/* Control-card header: status (left) + Create KR + ⋮ menu (right). */}
        <div className="obj-cc-top">
          <StatusPill status={objective.status} />
          {mayManageMembers && (
            <div className="obj-cc-top-actions" style={{ marginLeft: "auto" }}>
              <SpaceMembersButton spaceId={objectiveId} subjectLabel="objective" />
            </div>
          )}
          {mayEdit && (
          <div className="obj-cc-top-actions">
            <button type="button" className="btn obj-cc-create" onClick={() => setAddOpen(true)}>
              <Plus size={14} /> Create Key Result
            </button>
            <div className="obj-menu-wrap" ref={menuRef}>
              <button
                type="button"
                className="pc-overflow"
                title="More"
                onClick={() => setMenuOpen((o) => !o)}
              >
                <MoreVertical size={16} />
              </button>
              {menuOpen && (
                <div className="card-menu">
                  <button
                    type="button"
                    className="cm-item"
                    onClick={() => {
                      setMenuOpen(false);
                      setNameDraft(objective.name);
                      setEditingName(true);
                    }}
                  >
                    Rename objective
                  </button>
                  <button
                    type="button"
                    className="cm-item"
                    onClick={() => {
                      setMenuOpen(false);
                      const v = window.prompt("Objective description", objective.description ?? "");
                      if (v != null) patchObj.mutate({ description: v.trim() || null });
                    }}
                  >
                    Edit description
                  </button>
                  <div className="cm-sep" />
                  <div className="cm-label">Goal type</div>
                  {GOAL_TYPES.map((g) => (
                    <button
                      key={g.key}
                      type="button"
                      className={`cm-item cm-conf ${objective.goal_type === g.key ? "on" : ""}`}
                      title={g.help}
                      onClick={() => {
                        setMenuOpen(false);
                        patchObj.mutate({ goal_type: g.key });
                      }}
                    >
                      <span className={`goal-dot ${g.key}`} /> {g.label}
                      {objective.goal_type === g.key ? " ✓" : ""}
                    </button>
                  ))}
                  <div className="cm-sep" />
                  <button
                    type="button"
                    className="cm-item danger"
                    onClick={() => {
                      setMenuOpen(false);
                      archiveObj.mutate();
                    }}
                  >
                    Archive objective
                  </button>
                </div>
              )}
            </div>
          </div>
          )}
        </div>

        {editingName ? (
          <input
            className="obj-name-input"
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") setEditingName(false);
            }}
          />
        ) : (
          <h1 className="page" style={{ margin: 0 }}>
            {objective.name}
          </h1>
        )}

        {objective.description && (
          <p className="page-sub pc-desc-clamp" style={{ marginBottom: 18 }}>
            {objective.description}
          </p>
        )}

        <div className="obj-cc-prog">
          <div className="obj-cc-prog-top">
            <span className="obj-cc-pct">{headlinePct}%</span>
            <span className="obj-cc-lbl">progress</span>
          </div>
          <ProgressBar value={headlinePct} variant="kr" />

          {key_results.length > 0 && (
            <button
              type="button"
              className="obj-roll-toggle"
              onClick={() => setBreakdownOpen((o) => !o)}
              title="Show how this progress is calculated"
            >
              {roll.weighted ? "Weighted from" : "Averaged across"} {key_results.length} key
              result{key_results.length === 1 ? "" : "s"}
              <span className="obj-roll-caret">{breakdownOpen ? "▴" : "▾"}</span>
            </button>
          )}

          {key_results.length > 0 && (
            <div
              className="obj-conf-summary"
              title="Forward-looking confidence across this objective's key results"
            >
              <Target size={12} /> KR confidence: {confSummaryText(key_results).toLowerCase()}
            </div>
          )}

          {key_results.length > 0 && (
            <div className="obj-cc-reason">
              {objStatusReason(key_results, gt.key, objective.status, headlinePct)}
            </div>
          )}

          {breakdownOpen && roll.items.length > 0 && (
            <div className="obj-roll">
              <div className="obj-roll-row obj-roll-head">
                <span>Key result</span>
                <span>Progress</span>
                <span>Weight</span>
                <span>Contribution</span>
              </div>
              {roll.items.map((it) => (
                <div className="obj-roll-row" key={it.id}>
                  <span className="obj-roll-name" title={it.name}>
                    <span className="ref-id">{it.ref}</span>
                    {it.name}
                  </span>
                  <span>{it.progress}%</span>
                  <span className="obj-roll-w">{it.weightPct}%</span>
                  <span className="obj-roll-c">{Math.round(it.contribution)}%</span>
                </div>
              ))}
              <div className="obj-roll-row obj-roll-total">
                <span>Objective total</span>
                <span />
                <span />
                <span>{roll.progress}%</span>
              </div>
            </div>
          )}
        </div>

        {/* Meta line: owner avatar + name · target period · goal-type dot. */}
        <div className="obj-cc-meta">
          {ownerName && (
            <span className="obj-cc-owner">
              <span className="pc-avatar" title={ownerName}>
                {initials(ownerName)}
              </span>{" "}
              {ownerName}
            </span>
          )}
          {objective.period && (
            <span className="obj-cc-period">
              <Target size={13} /> {objective.period}
            </span>
          )}
          <span className="obj-cc-goaltype" title={gt.help}>
            <span className={`goal-dot ${gt.key}`} /> {gt.label}
          </span>
        </div>
      </div>
    </div>
  );

  // ── Below the sheet (desk): connected tools ────────────────────────────────
  const footer = (
    <>
      <LinkedIntegrations objectKind="objective" objectId={objectiveId} canEdit={mayEdit} />
    </>
  );

  // ── On the sheet (canvas is sacred): only the key results ──────────────────
  return (
    <DeskScreen toolbar={toolbar} footer={footer}>
      <div className="kr-head-row">
        <h2 className="sec" style={{ margin: 0 }}>
          Key Results{" "}
          <span style={{ fontWeight: 400, color: "var(--color-steel)" }}>
            ({key_results.length})
          </span>
        </h2>
      </div>

      <div className="kr-list">
        {key_results.length === 0 && (
          <div className="obj-empty">
            <div className="obj-empty-ico">◎</div>
            <div>
              <div className="obj-empty-h">No key results yet</div>
              <div className="obj-empty-d">
                Add the measurable outcomes that define success for this objective — use “Create
                Key Result” above.
              </div>
            </div>
          </div>
        )}

        {key_results.map((kr, i) => (
          <KrCard
            key={kr.id}
            kr={kr}
            krRef={`KR-${i + 1}`}
            objectiveSlug={objectiveSlug}
            invalidateKey={key}
            canEdit={mayEdit}
          />
        ))}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <div className="plan-skin">
            <DialogHeader>
              <DialogTitle>New key result</DialogTitle>
              <DialogDescription>
                A measurable outcome that defines success for this objective.
              </DialogDescription>
            </DialogHeader>
            <AddKrForm onCreate={(b) => addKr.mutate(b)} pending={addKr.isPending} />
          </div>
        </DialogContent>
      </Dialog>
    </DeskScreen>
  );
}

/** KR card (mockup .kr-card-v2) — confidence pill on top, KR ref + title,
 * current/target hero numbers + % with the kr-bar, a contributors summary line
 * (+ comment count), then an INLINE check-in composer revealed beneath it. The
 * title/progress block is a Link to the KR detail; the composer is kept OUTSIDE
 * the Link so its inputs/buttons don't trigger navigation, and mirrors the
 * KR-detail composer fields (value · confidence · note · optional blocker). */
function KrCard({
  kr,
  krRef,
  objectiveSlug,
  invalidateKey,
  canEdit = false,
}: {
  kr: KeyResultRead;
  krRef: string;
  objectiveSlug: string;
  invalidateKey: (string | undefined)[];
  /** editor+ — may check in / use the ⋮ Actions menu. Viewers see read-only. */
  canEdit?: boolean;
}) {
  const queryClient = useQueryClient();
  const [checking, setChecking] = useState(false);
  const [value, setValue] = useState(String(kr.current_value));
  const [confidence, setConfidence] = useState<string | null>(kr.confidence ?? null);
  const [note, setNote] = useState("");
  const [blocker, setBlocker] = useState("");

  const reset = () => {
    setValue(String(kr.current_value));
    setConfidence(kr.confidence ?? null);
    setNote("");
    setBlocker("");
  };

  const checkin = useMutation({
    mutationFn: () =>
      createCheckin(kr.id, {
        value: Number.parseFloat(value),
        confidence,
        note: note.trim() || null,
        blocker: blocker.trim() || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invalidateKey });
      void queryClient.invalidateQueries({ queryKey: ["plan", "kr", kr.id] });
      void queryClient.invalidateQueries({ queryKey: ["plan", "objectives"] });
      toast.success("Check-in recorded");
      setChecking(false);
      reset();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const valid = value !== "" && !Number.isNaN(Number.parseFloat(value));

  // ── ⋮ actions menu (mockup KRCard overflow) ──────────────────────────────
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const invalidateKr = () => {
    void queryClient.invalidateQueries({ queryKey: invalidateKey });
    void queryClient.invalidateQueries({ queryKey: ["plan", "kr", kr.id] });
    void queryClient.invalidateQueries({ queryKey: ["plan", "objectives"] });
  };
  const patchKr = useMutation({
    mutationFn: (b: Parameters<typeof patchKeyResult>[1]) => patchKeyResult(kr.id, b),
    onSuccess: invalidateKr,
    onError: (e) => toast.error((e as Error).message),
  });
  const delKr = useMutation({
    mutationFn: () => deleteKeyResult(kr.id),
    onSuccess: () => {
      invalidateKr();
      toast.success("Key result deleted");
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const closeMore = () => setMoreOpen(false);
  const askText = (label: string, current: string | null | undefined, field: "name" | "unit") => {
    const v = window.prompt(label, current ?? "");
    if (v != null) patchKr.mutate({ [field]: v.trim() || null } as { name?: string; unit?: string | null });
  };
  const askNum = (
    label: string,
    current: number | null | undefined,
    field: "current_value" | "target_value" | "weight",
  ) => {
    const v = window.prompt(label, current != null ? String(current) : "");
    if (v == null) return;
    const n = Number.parseFloat(v);
    if (!Number.isNaN(n)) patchKr.mutate({ [field]: n });
  };

  // Forward-looking CONFIDENCE pill — not a progress band. A KR can be 40% and
  // On Track, or 70% and At Risk. Falls back to a neutral "No signal" chip.
  const confTone = pillClass(kr.confidence ?? "");
  const confText = confidenceLabel(kr.confidence);
  const confTitle = kr.confidence_note ?? (kr.confidence ? "" : "No confidence update yet");

  // Contributors can be projects or initiatives — count comes from the API.
  const contribN = kr.contributor_count ?? 0;
  const contribSummary =
    contribN === 0
      ? "No contributors yet"
      : `${contribN} Contributor${contribN === 1 ? "" : "s"}`;
  const commentN = kr.comment_count ?? 0;
  const last = kr.last_checkin ?? null;

  return (
    <div className="kr-card kr-card-click kr-card-v2">
      {/* Head sits OUTSIDE the Link so the ⋮ menu's buttons don't navigate. */}
      <div className="kr-card-head">
        <span className={`status-pill ${confTone} kr-status kr-conf-pill`} title={confTitle}>
          <span className="sp-dot" /> {confText}
        </span>
        {canEdit && (
        <div className="kr-more-wrap" ref={moreRef}>
          <button
            type="button"
            className="pc-overflow"
            title="Actions"
            onClick={() => setMoreOpen((o) => !o)}
          >
            <MoreVertical size={15} />
          </button>
          {moreOpen && (
            <div className="card-menu">
              <button
                type="button"
                className="cm-item"
                onClick={() => {
                  closeMore();
                  askNum("Current value", kr.current_value, "current_value");
                }}
              >
                Edit progress
              </button>
              <button
                type="button"
                className="cm-item"
                onClick={() => {
                  closeMore();
                  askText("Key result title", kr.name, "name");
                }}
              >
                Edit title
              </button>
              <button
                type="button"
                className="cm-item"
                onClick={() => {
                  closeMore();
                  askNum("Target value", kr.target_value, "target_value");
                }}
              >
                Update target
              </button>
              <button
                type="button"
                className="cm-item"
                onClick={() => {
                  closeMore();
                  askText("Unit (e.g. %, users, $)", kr.unit, "unit");
                }}
              >
                Set unit
              </button>
              <button
                type="button"
                className="cm-item"
                onClick={() => {
                  closeMore();
                  askNum(
                    "Weight toward the objective (share %). Leave blank for equal weighting.",
                    kr.weight,
                    "weight",
                  );
                }}
              >
                Set weight
              </button>
              <div className="cm-sep" />
              <div className="cm-label">Confidence</div>
              {CONFIDENCE_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`cm-item cm-conf ${kr.confidence === c ? "on" : ""}`}
                  onClick={() => {
                    closeMore();
                    patchKr.mutate({ confidence: c, confidence_note: null });
                  }}
                >
                  <span className={`cm-conf-dot ${pillClass(c)}`} /> Mark {confidenceLabel(c)}
                  {kr.confidence === c ? " ✓" : ""}
                </button>
              ))}
              {kr.confidence && (
                <button
                  type="button"
                  className="cm-item"
                  onClick={() => {
                    closeMore();
                    patchKr.mutate({ confidence: null, confidence_note: null });
                  }}
                >
                  Clear confidence
                </button>
              )}
              <div className="cm-sep" />
              <button
                type="button"
                className="cm-item danger"
                onClick={() => {
                  closeMore();
                  if (window.confirm(`Delete "${kr.name}"?`)) delKr.mutate();
                }}
              >
                Delete key result
              </button>
            </div>
          )}
        </div>
        )}
      </div>

      <Link
        to="/plan/o/$objectiveSlug/kr/$krId"
        params={{ objectiveSlug, krId: kr.id }}
        className="kr-card-link"
      >
        <div className="kr-ref">{krRef}</div>
        <div className="kr-title">{kr.name}</div>

        <div className="kr-progress">
          <div className="kr-prog-nums">
            <b>{fmt(kr.current_value)}</b> / {kr.target_value != null ? fmt(kr.target_value) : "—"}{" "}
            {kr.unit ?? ""}
            <span className="kr-pct">{kr.progress}%</span>
          </div>
          <ProgressBar value={kr.progress} variant="kr" />
        </div>

        <div className="kr-meta-row">
          <span className="kr-contrib-summary">
            <FileText size={13} /> {contribSummary}
            {kr.weight > 0 && (
              <span className="kr-weight-inline static" title="Share of objective progress">
                {" · "}
                {kr.weight}%
              </span>
            )}
          </span>
          {commentN > 0 && (
            <span
              className="kr-cmt-count"
              title={`${commentN} comment${commentN === 1 ? "" : "s"} — open the key result to discuss`}
            >
              💬 {commentN}
            </span>
          )}
        </div>
      </Link>

      {/* Check-in row — the OKR update ritual. History lives on the KR detail. */}
      <div className="kr-checkin-row">
        <span className="kr-checkin-meta">
          <FileText size={12} />{" "}
          {last ? `Last checked in — ${fmt(last.value)}${kr.unit ?? ""}` : "No check-ins yet"}
        </span>
        {canEdit && !checking && (
          <button
            type="button"
            className="kr-checkin-btn"
            onClick={() => {
              reset();
              setChecking(true);
            }}
          >
            {last ? "+ Check in" : "+ Add first check-in"}
          </button>
        )}
      </div>

      {checking && (
        <div className="checkin-composer compact">
          <div className="ci-top">
            <div className="ci-field">
              <span className="ci-label">New value</span>
              <span className="ci-val">
                <input
                  type="number"
                  className="input"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
                <span className="ci-unit">
                  / {kr.target_value ?? "—"} {kr.unit ?? ""}
                </span>
              </span>
            </div>
            <div className="ci-field">
              <span className="ci-label">Confidence</span>
              <div className="ci-conf">
                {CONFIDENCE_OPTIONS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setConfidence(c)}
                    className={`ci-conf-btn ${pillClass(c)}${confidence === c ? " on" : ""}`}
                  >
                    {confidenceLabel(c)}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <input
            className="input"
            autoFocus
            placeholder="What changed? (short note)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <input
            className="input"
            placeholder="Blocker / risk (optional)"
            value={blocker}
            onChange={(e) => setBlocker(e.target.value)}
          />
          <div className="ci-actions">
            <button
              type="button"
              className="btn primary sm"
              disabled={!valid || checkin.isPending}
              onClick={() => checkin.mutate()}
            >
              {checkin.isPending ? "Recording…" : "Check in"}
            </button>
            <button type="button" className="btn sm" onClick={() => setChecking(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Inline KR create — name + target + unit. A target is what lets KR progress
 * (current/target) move off 0, so it's collected at creation time. */
function AddKrForm({
  onCreate,
  pending,
}: {
  onCreate: (b: KeyResultCreate) => void;
  pending: boolean;
}) {
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [unit, setUnit] = useState("");
  const submit = () => {
    const n = name.trim();
    if (!n || pending) return;
    const t = Number.parseFloat(target);
    onCreate({
      name: n,
      target_value: Number.isNaN(t) ? null : t,
      unit: unit.trim() || null,
    });
    setName("");
    setTarget("");
    setUnit("");
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="mt-2 flex flex-col gap-4"
    >
      <div className="field">
        <span className="field-row-label">Name</span>
        <input
          className="input"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Reach 50k weekly active users"
        />
      </div>
      <div className="flex gap-3">
        <div className="field flex-1">
          <span className="field-row-label">Target</span>
          <input
            className="input"
            type="number"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="e.g. 50000"
          />
        </div>
        <div className="field flex-1">
          <span className="field-row-label">Unit</span>
          <input
            className="input"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="e.g. users, %, $"
          />
        </div>
      </div>
      <DialogFooter>
        <button type="submit" className="btn primary" disabled={!name.trim() || pending}>
          {pending ? "Adding…" : "Add key result"}
        </button>
      </DialogFooter>
    </form>
  );
}